/**
 * 客户细分「嵌套条件树」纯逻辑工具
 * 树节点两类：
 *   condition: { type:'condition', field, operator, value }
 *   group:     { type:'group', logic:'AND'|'OR', children:[...] }
 * 同时兼容老扁平格式：{ conditions:[...], matchLogic:'AND'|'OR' }
 */

// 最大嵌套深度，需与后端 MAX_RULE_DEPTH 保持一致。
// 前端按"分组层级"计（根分组=1），编辑器最多可建 5 层分组；
// 后端按"节点"计深度，最深可建树的条件叶子恰好落在后端深度 5（边界，仍被求值）。
export const MAX_RULE_DEPTH = 5;

// 操作符可读符号（用于摘要展示）
const OP_TEXT = {
  eq: '=', equals: '=',
  neq: '≠', notEquals: '≠',
  gt: '>', greaterThan: '>',
  gte: '≥', greaterThanOrEqual: '≥',
  lt: '<', lessThan: '<',
  lte: '≤', lessThanOrEqual: '≤',
  contains: '包含',
  not_contains: '不含',
  startsWith: '开头是',
  endsWith: '结尾是',
};

// 为树节点生成稳定 id，供前端列表 key 使用（后端求值忽略该字段）
let _idSeq = 0;
function nextId() {
  return `n${Date.now().toString(36)}${(_idSeq++).toString(36)}`;
}

// 新建一个空条件
export function emptyCondition() {
  return { id: nextId(), type: 'condition', field: 'totalSpent', operator: 'gte', value: '' };
}

// 新建一个含单条件的空分组
export function emptyGroup(logic = 'AND') {
  return { id: nextId(), type: 'group', logic, children: [emptyCondition()] };
}

// 判断是否分组节点
function isGroup(node) {
  return node && (node.type === 'group' || Array.isArray(node.children));
}

/**
 * 规范化为根 group 节点。
 * 老扁平格式 → 包装成 group；新 group → 深拷贝；null/非法 → 默认空 group。
 */
export function normalizeToTree(rules) {
  if (!rules || typeof rules !== 'object') {
    return emptyGroup();
  }
  // 老扁平格式
  if (Array.isArray(rules.conditions)) {
    return {
      id: nextId(),
      type: 'group',
      logic: rules.matchLogic === 'OR' ? 'OR' : 'AND',
      children: rules.conditions.map((c) => ({
        id: nextId(),
        type: 'condition',
        field: c.field,
        operator: c.operator,
        value: c.value,
      })),
    };
  }
  // 新格式
  if (isGroup(rules)) {
    return cloneNode(rules);
  }
  return emptyGroup();
}

function cloneNode(node) {
  if (isGroup(node)) {
    return {
      id: node.id || nextId(),
      type: 'group',
      logic: node.logic === 'OR' ? 'OR' : 'AND',
      children: (node.children || []).map(cloneNode),
    };
  }
  return { id: node.id || nextId(), type: 'condition', field: node.field, operator: node.operator, value: node.value };
}

/**
 * 剪枝：去掉 value 为空/未填的条件，以及剪枝后 children 为空的 group。
 * 返回剪枝后的节点，若整棵为空返回 null。
 */
export function pruneTree(node) {
  if (!isGroup(node)) {
    const v = node && node.value;
    if (v === '' || v === null || v === undefined) return null;
    // 保留 id 以保持列表 key 稳定
    return { id: node.id, type: 'condition', field: node.field, operator: node.operator, value: node.value };
  }
  const children = (node.children || [])
    .map(pruneTree)
    .filter((c) => c !== null);
  if (children.length === 0) return null;
  // 保留 id 以保持列表 key 稳定
  return { id: node.id, type: 'group', logic: node.logic === 'OR' ? 'OR' : 'AND', children };
}

/**
 * 树 → 带括号可读字符串。
 * opts.fieldLabel(field) 返回字段中文名；opts.opText(op) 可选覆盖操作符文本。
 */
export function stringifyRuleTree(node, opts = {}) {
  const fieldLabel = opts.fieldLabel || ((f) => f);
  const opText = opts.opText || ((op) => OP_TEXT[op] || op);
  if (!node) return '';
  if (!isGroup(node)) {
    return `${fieldLabel(node.field)} ${opText(node.operator)} ${node.value}`;
  }
  const sep = node.logic === 'OR' ? ' 或 ' : ' 且 ';
  const parts = (node.children || []).map((child) => {
    const s = stringifyRuleTree(child, opts);
    // 子分组加括号
    return isGroup(child) ? `(${s})` : s;
  });
  return parts.join(sep);
}

/**
 * 是否为嵌套规则（存在 group 类型的子节点）——移动端据此禁用嵌套编辑。
 */
export function isNestedRules(rules) {
  const tree = normalizeToTree(rules);
  return (tree.children || []).some((c) => isGroup(c));
}

/**
 * 最大分组深度（根 group 记为 1）。
 */
export function treeMaxDepth(node) {
  if (!isGroup(node)) return 0;
  const childDepths = (node.children || []).map(treeMaxDepth);
  return 1 + (childDepths.length ? Math.max(...childDepths) : 0);
}
